import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { FiUsers, FiDollarSign, FiEye, FiTrendingUp, FiGlobe, FiChevronRight, FiLogOut, FiFileText, FiTrash2, FiEdit2, FiLayers, FiAlertTriangle, FiShield, FiExternalLink, FiCreditCard, FiPercent, FiBriefcase, FiEyeOff, FiMessageCircle, FiStar } from "react-icons/fi";
import AdminLandingTab from "./AdminLandingTab";
import AdminTransactionsTab from "./AdminTransactionsTab";
import AdminFeesTab from "./AdminFeesTab";
import AdminUsersTab from "./AdminUsersTab";
import AdminBusinessesTab from "./AdminBusinessesTab";
import AdminBlockedUploadsTab from "./AdminBlockedUploadsTab";
import AdminCampaignsTab from "./AdminCampaignsTab";
import AdminSupportTab from "./AdminSupportTab";
import { useTranslation } from "../context/TranslationContext";
import { ADMIN_TONES, adminTheme } from "../styles/adminTheme";
import { socketUrl } from "../config/socketUrl";
import {
  getAdminStats,
  getIPs,
  getIPDetails,
  getPlatformEarnings,
  getAdminBugReports,
  updateBugReportStatus,
  adminListNews,
  adminCreateNews,
  adminUpdateNews,
  adminDeleteNews,
  adminListTutorials,
  adminCreateTutorial,
  adminUpdateTutorial,
  adminDeleteTutorial,
  adminUploadImage,
  adminUploadVideo,
  getAdminAppeals,
  resolveAdminAppeal,
  getAdminSupportConversations,
} from "../api/admin";
import BackblazeUploadButton from "../components/BackblazeUploadButton";
import UserProfileLink from "../components/UserProfileLink";

/** A section in the sidebar; the current one carries the brand colour and a marker. */
function AdminNavButton({ tab, active, isDark, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition ${
        active
          ? isDark
            ? "bg-mint/10 text-mint"
            : "bg-mint/15 text-emerald-700"
          : "text-label-secondary hover:bg-fill-quaternary hover:text-label-primary"
      }`}
    >
      {active && (
        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-mint" aria-hidden />
      )}
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{tab.label}</span>
      {tab.badge > 0 && (
        <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-mint px-1.5 text-[11px] font-bold text-ink">
          {tab.badge > 99 ? "99+" : tab.badge}
        </span>
      )}
    </button>
  );
}

/** One figure on the dashboard, with a tinted icon. */
function StatCard({ theme, icon: Icon, tone = "mint", label, value, note }) {
  const colors = ADMIN_TONES[tone] || ADMIN_TONES.mint;
  return (
    <div className={`${theme.cardBase} rounded-2xl p-5`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`text-xs font-medium ${theme.mutedText}`}>{label}</span>
        <span className={`grid h-9 w-9 place-items-center rounded-xl ring-1 ring-inset ${colors.chip}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className={`mt-3 text-3xl font-bold tracking-tight ${theme.baseText}`}>{value}</div>
      {note && <div className={`mt-1 text-xs ${theme.mutedText}`}>{note}</div>}
    </div>
  );
}

const AdminDashboardPage = ({ isDark, setToast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [ips, setIPs] = useState([]);
  const [selectedIP, setSelectedIP] = useState(null);
  const [ipDetails, setIPDetails] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsSubLoading, setNewsSubLoading] = useState(false);
  const [newsForm, setNewsForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    coverImageUrl: "",
    published: true,
    sortOrder: 0,
  });
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [tutorialForm, setTutorialForm] = useState({
    title: "",
    description: "",
    videoUrl: "",
    thumbnailUrl: "",
    order: 0,
    published: true,
  });
  const [editingTutorialId, setEditingTutorialId] = useState(null);
  const [bugReports, setBugReports] = useState([]);
  const [bugReportsTotal, setBugReportsTotal] = useState(0);
  const [bugReportsLoading, setBugReportsLoading] = useState(false);
  const [bugStatusFilter, setBugStatusFilter] = useState("");
  const [appeals, setAppeals] = useState([]);
  const [appealsLoading, setAppealsLoading] = useState(false);
  const [appealsStatus, setAppealsStatus] = useState("open");
  const [appealNotes, setAppealNotes] = useState({});
  const [appealActionId, setAppealActionId] = useState(null);
  /** Support conversations with messages no admin has read, for the badge on Support. */
  const [supportUnread, setSupportUnread] = useState(0);
  /** This admin's socket, in the room every admin shares; the Support tab listens on it too. */
  const [supportSocket, setSupportSocket] = useState(null);

  const theme = adminTheme(isDark);
  const { baseText, mutedText, cardBase, inputBase, inset } = theme;

  /** The sections, grouped the way an admin looks for them. */
  const navGroups = [
    {
      label: "Overview",
      items: [
        { id: "dashboard", label: "Dashboard", icon: FiTrendingUp },
        { id: "earnings", label: "Platform Earnings", icon: FiDollarSign },
        { id: "ips", label: "IP Tracking", icon: FiGlobe },
      ],
    },
    {
      label: "Money",
      items: [
        { id: "transactions", label: "Transactions", icon: FiCreditCard },
        { id: "fees", label: "Fees", icon: FiPercent },
      ],
    },
    {
      label: "People",
      items: [
        { id: "users", label: "Users", icon: FiUsers },
        { id: "businesses", label: "Businesses", icon: FiBriefcase },
        { id: "support", label: t("admin.support"), icon: FiMessageCircle, badge: supportUnread },
      ],
    },
    {
      label: "Moderation",
      items: [
        { id: "appeals", label: "Appeals", icon: FiShield },
        { id: "blocked-uploads", label: t("admin.blockedUploads"), icon: FiEyeOff },
        { id: "bugs", label: t("admin.bugReports"), icon: FiAlertTriangle },
      ],
    },
    {
      label: "Content",
      items: [
        { id: "featured", label: t("admin.featuredCampaigns"), icon: FiStar },
        { id: "news", label: "News & tutorials", icon: FiFileText },
        { id: "landing", label: "Landing page", icon: FiLayers },
      ],
    },
  ];
  const currentGroup = navGroups.find((g) => g.items.some((i) => i.id === activeTab)) || navGroups[0];
  const currentTab = currentGroup.items.find((i) => i.id === activeTab) || currentGroup.items[0];

  useEffect(() => {
    // Check if admin is logged in
    const adminToken = localStorage.getItem("adminToken");
    
    if (!adminToken) {
      setTimeout(() => {
        window.location.href = "/admin";
      }, 100);
      return;
    }

    // Set user from localStorage or empty object
    try {
      const savedAdmin = localStorage.getItem("adminUser");
      if (savedAdmin) {
        const user = JSON.parse(savedAdmin);
        setAdminUser(user || {});
      }
    } catch (err) {
      // Ignore parse errors
    }

    fetchDashboardStats();
  }, []);

  // The Support badge, live wherever the admin is in the panel.
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) return undefined;
    const refresh = () =>
      getAdminSupportConversations({ limit: 1 })
        .then((res) => setSupportUnread(res.counts?.unread || 0))
        .catch(() => {});
    let timer;
    const soon = () => {
      clearTimeout(timer);
      timer = setTimeout(refresh, 500);
    };
    const socket = io(socketUrl(), { auth: { token }, transports: ["websocket", "polling"], reconnection: true });
    socket.on("connect", () => socket.emit("join_support_admins"));
    socket.on("support_message", soon);
    socket.on("support_read", soon);
    socket.on("support_status", soon);
    setSupportSocket(socket);
    refresh();
    // Should the socket drop for good, the badge still catches up.
    const id = setInterval(refresh, 60000);
    return () => {
      clearInterval(id);
      clearTimeout(timer);
      socket.close();
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    window.location.href = "/admin";
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await getAdminStats();
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load admin stats");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchIPs = async () => {
    try {
      const res = await getIPs();
      if (res.success) {
        setIPs(res.data);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load IPs");
      }
    }
  };

  const fetchIPDetails = async (ip) => {
    try {
      const res = await getIPDetails(ip);
      if (res.success) {
        setIPDetails(res.data);
        setSelectedIP(ip);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load IP details");
      }
    }
  };

  const fetchEarnings = async () => {
    try {
      const res = await getPlatformEarnings();
      if (res.success) {
        setEarnings(res.data);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load earnings");
      }
    }
  };

  const fetchNewsAdmin = async () => {
    try {
      setNewsSubLoading(true);
      const res = await adminListNews();
      if (res.success) setNewsArticles(res.data || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load news");
      }
    } finally {
      setNewsSubLoading(false);
    }
  };

  const fetchTutorialsAdmin = async () => {
    try {
      const res = await adminListTutorials();
      if (res.success) setTutorials(res.data || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load tutorials");
      }
    }
  };

  const fetchBugReports = async () => {
    try {
      setBugReportsLoading(true);
      const params = { limit: 50 };
      if (bugStatusFilter) params.status = bugStatusFilter;
      const res = await getAdminBugReports(params);
      if (res.success) {
        setBugReports(res.data || []);
        setBugReportsTotal(typeof res.total === "number" ? res.total : (res.data || []).length);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load bug reports");
      }
    } finally {
      setBugReportsLoading(false);
    }
  };

  const fetchAppeals = async () => {
    try {
      setAppealsLoading(true);
      const res = await getAdminAppeals(appealsStatus);
      if (res.success) setAppeals(res.data || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        window.location.href = "/admin";
      } else {
        setToast?.(err?.response?.data?.message || "Failed to load appeals");
      }
    } finally {
      setAppealsLoading(false);
    }
  };

  const handleResolveAppeal = async (submissionId, decision) => {
    setAppealActionId(submissionId + decision);
    try {
      const res = await resolveAdminAppeal(submissionId, decision, appealNotes[submissionId] || "");
      if (res.success) {
        setToast?.(decision === "approved" ? "Appeal approved" : "Appeal denied");
        setAppeals((prev) => prev.filter((a) => a._id !== submissionId));
      }
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Failed to resolve appeal");
    } finally {
      setAppealActionId(null);
    }
  };

  useEffect(() => {
    if (activeTab === "ips") {
      fetchIPs();
    } else if (activeTab === "earnings") {
      fetchEarnings();
    } else if (activeTab === "news") {
      fetchNewsAdmin();
      fetchTutorialsAdmin();
    } else if (activeTab === "bugs") {
      fetchBugReports();
    } else if (activeTab === "appeals") {
      fetchAppeals();
    }
  }, [activeTab, bugStatusFilter, appealsStatus]);

  // On narrow screens the sections are a scrolling row of pills; keep the current one in view.
  useEffect(() => {
    document
      .querySelector(`[data-admin-pill="${activeTab}"]`)
      ?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [activeTab]);

  const handleBugReportStatusChange = async (id, status) => {
    try {
      const res = await updateBugReportStatus(id, status);
      if (res.success && res.data) {
        setBugReports((prev) => prev.map((b) => (b._id === id ? { ...b, ...res.data } : b)));
        setToast?.(t("admin.bugStatusUpdated"));
      }
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("common.error"));
    }
  };

  if (loading) {
    return (
      <div className={`flex h-screen items-center justify-center ${theme.page}`}>
        <div className={`flex items-center gap-3 text-sm ${mutedText}`}>
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-mint/30 border-t-mint" />
          {t("common.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen font-ds ${theme.page}`} style={{ backgroundImage: theme.pageGlow }}>
      <div className="lg:flex">
        {/* Sidebar — desktop */}
        <aside
          className={`hidden border-r backdrop-blur lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col ${theme.sidebar}`}
        >
          <div className="flex items-center gap-2.5 px-5 pb-4 pt-6">
            <img src="/assets/mintly-logo.png" alt="Mintly" className="h-7 w-auto" />
            <span className="rounded-md bg-mint/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-mint">
              Admin
            </span>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="Admin sections">
            {navGroups.map((group) => (
              <div key={group.label} className="mt-5 first:mt-2">
                <p className={`px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-label-tertiary`}>
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((tab) => (
                    <AdminNavButton
                      key={tab.id}
                      tab={tab}
                      active={activeTab === tab.id}
                      isDark={isDark}
                      onClick={() => setActiveTab(tab.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className={`border-t px-4 py-4 ${theme.rowBorder}`}>
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-mint/15 text-sm font-bold text-mint">
                {String(adminUser?.username || "A").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`truncate text-sm font-semibold ${baseText}`}>{adminUser?.username || "Admin"}</div>
                {adminUser?.email && <div className={`truncate text-[11px] ${mutedText}`}>{adminUser.email}</div>}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className={theme.iconBtn}
                aria-label="Logout"
                title="Logout"
              >
                <FiLogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* Top bar — phones and tablets */}
          <div className={`sticky top-0 z-30 border-b backdrop-blur-xl lg:hidden ${theme.sidebar}`}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <img src="/assets/mintly-logo.png" alt="Mintly" className="h-6 w-auto" />
                <span className="rounded-md bg-mint/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-mint">
                  Admin
                </span>
              </div>
              <button type="button" onClick={handleLogout} className={theme.btnSecondary}>
                <FiLogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
            <div className="flex gap-1.5 overflow-x-auto px-4 pb-3">
              {navGroups.flatMap((g) => g.items).map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-admin-pill={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      active
                        ? "bg-mint text-ink"
                        : isDark
                        ? "bg-white/[0.05] text-slate-300 hover:bg-white/[0.08]"
                        : "border border-slate-200 bg-white text-slate-600"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label}
                    {tab.badge > 0 && (
                      <span className={`rounded-full px-1.5 text-[10px] font-bold ${active ? "bg-ink/15" : "bg-mint text-ink"}`}>
                        {tab.badge > 99 ? "99+" : tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-4 py-6 sm:px-6 lg:px-10 lg:py-9">
            <header>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-mint/80">{currentGroup.label}</p>
              <h1 className={`mt-1 text-2xl font-bold tracking-tight sm:text-[1.7rem] ${baseText}`}>{currentTab.label}</h1>
            </header>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard theme={theme} icon={FiUsers} tone="mint" label="Users" value={stats.users.total} />
              <StatCard theme={theme} icon={FiTrendingUp} tone="aqua" label="Campaigns" value={stats.campaigns.total} />
              <StatCard
                theme={theme}
                icon={FiEye}
                tone="emerald"
                label="Unique IPs"
                value={stats.visits.uniqueIPs}
                note={`${stats.visits.totalVisits} total visits`}
              />
              <StatCard
                theme={theme}
                icon={FiDollarSign}
                tone="amber"
                label="Platform Earnings"
                value={`${parseFloat(stats.earnings.platformEarnings).toFixed(2)} ₾`}
                note={`${stats.earnings.platformFeePercentage}% fee`}
              />
            </div>

            {/* Submissions breakdown */}
            <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className={theme.heading}>Submissions</h3>
                <span className={`text-sm ${mutedText}`}>{stats.submissions.total} total</span>
              </div>
              <div className={`mt-4 flex h-2 overflow-hidden rounded-full bg-fill-primary`}>
                {[
                  ["approved", "bg-emerald-400"],
                  ["pending", "bg-amber-400"],
                  ["rejected", "bg-rose-400"],
                ].map(([key, color]) => (
                  <div
                    key={key}
                    className={`${color} transition-all`}
                    style={{
                      width: `${stats.submissions.total ? (stats.submissions[key] / stats.submissions.total) * 100 : 0}%`,
                    }}
                  />
                ))}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  ["Total", stats.submissions.total, baseText, null],
                  ["Approved", stats.submissions.approved, "text-emerald-400", "bg-emerald-400"],
                  ["Pending", stats.submissions.pending, "text-amber-400", "bg-amber-400"],
                  ["Rejected", stats.submissions.rejected, "text-rose-400", "bg-rose-400"],
                ].map(([label, value, color, dot]) => (
                  <div key={label} className={`rounded-xl p-3.5 ${inset}`}>
                    <div className={`flex items-center gap-1.5 text-xs ${mutedText}`}>
                      {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
                      {label}
                    </div>
                    <div className={`mt-1 text-2xl font-bold tracking-tight ${color}`}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* IP Tracking Tab */}
        {activeTab === "ips" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* IP List */}
            <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
              <h3 className={`text-base font-semibold tracking-tight ${baseText} mb-4`}>Unique IPs ({ips.length})</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {ips.map((ipStat) => (
                  <button
                    key={ipStat.ip}
                    onClick={() => fetchIPDetails(ipStat.ip)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition ${
                      selectedIP === ipStat.ip
                        ? "border-mint/40 bg-mint/10"
                        : isDark
                        ? "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]"
                        : "border-slate-200/80 bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-mono font-semibold ${baseText}`}>{ipStat.ip}</div>
                        <div className={`text-xs ${mutedText} mt-1`}>
                          {ipStat.visitCount} visits
                        </div>
                      </div>
                      <FiChevronRight className={`h-5 w-5 ${mutedText}`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* IP Details */}
            {ipDetails && (
              <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>IP: {ipDetails.ip}</h3>
                  <button
                    onClick={() => {
                      setSelectedIP(null);
                      setIPDetails(null);
                    }}
                    className={`${theme.btnSecondary} px-2.5 py-1 text-xs`}
                  >
                    Close
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className={`text-xs ${mutedText}`}>Total Visits</div>
                    <div className={`text-2xl font-bold ${baseText}`}>{ipDetails.totalVisits}</div>
                  </div>
                  <div>
                    <div className={`text-xs ${mutedText}`}>Unique Pages</div>
                    <div className={`text-xl font-bold ${baseText}`}>{ipDetails.uniquePaths}</div>
                  </div>
                  <div>
                    <div className={`text-xs ${mutedText} mb-2`}>Pages Visited</div>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {ipDetails.paths.map((path) => (
                        <div key={path} className={`text-sm font-mono ${baseText} px-3 py-2 rounded-lg ${inset}`}>
                          {path}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${mutedText} mb-2`}>Recent Visits</div>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {ipDetails.visits.slice(0, 20).map((visit, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${inset}`}
                        >
                          <div className={`text-sm font-mono ${baseText}`}>{visit.path}</div>
                          <div className={`text-xs ${mutedText} mt-1`}>
                            {new Date(visit.visitedAt).toLocaleString()}
                          </div>
                          {visit.username && (
                            <div className={`text-xs ${mutedText} mt-1`}>
                              User:{" "}
                              <UserProfileLink
                                user={visit.userId ? { _id: visit.userId, username: visit.username } : null}
                                avatarSize="xs"
                                isDark={isDark}
                                fallback={visit.username}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === "earnings" && earnings && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
                <div className={`text-xs font-medium ${mutedText} mb-2`}>Total Platform Earnings</div>
                <div className={`text-2xl font-bold tracking-tight text-mint`}>
                  {parseFloat(earnings.summary.totalPlatformEarnings).toFixed(2)} ₾
                </div>
              </div>
              <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
                <div className={`text-xs font-medium ${mutedText} mb-2`}>Total Creator Earnings</div>
                <div className={`text-2xl font-bold tracking-tight text-emerald-400`}>
                  {parseFloat(earnings.summary.totalCreatorEarnings).toFixed(2)} ₾
                </div>
              </div>
              <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
                <div className={`text-xs font-medium ${mutedText} mb-2`}>Total Gross</div>
                <div className={`text-2xl font-bold tracking-tight ${baseText}`}>
                  {parseFloat(earnings.summary.totalGrossEarnings).toFixed(2)} ₾
                </div>
                <div className={`text-sm ${mutedText} mt-2`}>
                  {earnings.summary.platformFeePercentage}% platform fee
                </div>
              </div>
            </div>

            {/* Daily Breakdown */}
            <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
              <h3 className={`text-base font-semibold tracking-tight ${baseText} mb-4`}>Daily Earnings Breakdown</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {earnings.dailyBreakdown.map((day) => (
                  <div
                    key={day.date}
                    className={`p-4 rounded-xl ${inset}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`font-semibold ${baseText}`}>{day.date}</div>
                      <div className={`text-sm ${mutedText}`}>{day.submissions} submissions</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className={mutedText}>Platform</div>
                        <div className={`font-bold text-mint`}>{day.platformFee.toFixed(2)} ₾</div>
                      </div>
                      <div>
                        <div className={mutedText}>Creators</div>
                        <div className={`font-bold text-emerald-400`}>{day.creatorEarned.toFixed(2)} ₾</div>
                      </div>
                      <div>
                        <div className={mutedText}>Gross</div>
                        <div className={`font-bold ${baseText}`}>{day.grossEarnings.toFixed(2)} ₾</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detailed Breakdown */}
            <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
              <h3 className={`text-base font-semibold tracking-tight ${baseText} mb-4`}>
                Submission Earnings ({earnings.earningsBreakdown.length})
              </h3>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {earnings.earningsBreakdown.map((earning) => (
                  <div
                    key={earning.submissionId}
                    className={`p-4 rounded-xl ${inset}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className={`font-semibold ${baseText}`}>{earning.campaignName}</div>
                        <div className={`text-xs ${mutedText}`}>
                          {earning.creator} • {earning.platform} • {earning.views.toLocaleString()} views
                        </div>
                      </div>
                      <div className={`text-xs ${mutedText}`}>
                        {new Date(earning.approvedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                      <div>
                        <div className={mutedText}>Platform Fee</div>
                        <div className={`font-bold text-mint`}>{parseFloat(earning.platformFee).toFixed(2)} ₾</div>
                      </div>
                      <div>
                        <div className={mutedText}>Creator Earned</div>
                        <div className={`font-bold text-emerald-400`}>{parseFloat(earning.creatorEarned).toFixed(2)} ₾</div>
                      </div>
                      <div>
                        <div className={mutedText}>Gross</div>
                        <div className={`font-bold ${baseText}`}>{parseFloat(earning.grossEarnings).toFixed(2)} ₾</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* News & tutorials */}
        {activeTab === "news" && (
          <div className="space-y-10">
            <div>
              <h2 className={`text-base font-semibold tracking-tight ${baseText} mb-3`}>News articles</h2>
              {newsSubLoading ? (
                <div className={mutedText}>Loading…</div>
              ) : (
                <div className="space-y-3 max-h-72 overflow-y-auto mb-6">
                  {(newsArticles || []).map((a) => (
                    <div
                      key={a._id}
                      className={`flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl ${inset}`}
                    >
                      <div>
                        <div className={`font-semibold ${baseText}`}>{a.title}</div>
                        <div className={`text-xs ${mutedText}`}>
                          /resources/news/{a.slug} · {a.published ? "Published" : "Draft"}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingNewsId(a._id);
                            setNewsForm({
                              title: a.title || "",
                              slug: a.slug || "",
                              excerpt: a.excerpt || "",
                              content: a.content || "",
                              coverImageUrl: a.coverImageUrl || "",
                              published: !!a.published,
                              sortOrder: a.sortOrder ?? 0,
                            });
                          }}
                          className={theme.iconBtn}
                          title="Edit"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm("Delete this article?")) return;
                            try {
                              await adminDeleteNews(a._id);
                              setToast?.("Deleted");
                              fetchNewsAdmin();
                            } catch (err) {
                              setToast?.(err?.response?.data?.message || "Delete failed");
                            }
                          }}
                          className={theme.dangerIconBtn}
                          title="Delete"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingNewsId) {
                      await adminUpdateNews(editingNewsId, newsForm);
                      setToast?.("Article updated");
                    } else {
                      await adminCreateNews(newsForm);
                      setToast?.("Article created");
                    }
                    setEditingNewsId(null);
                    setNewsForm({
                      title: "",
                      slug: "",
                      excerpt: "",
                      content: "",
                      coverImageUrl: "",
                      published: true,
                      sortOrder: 0,
                    });
                    fetchNewsAdmin();
                  } catch (err) {
                    setToast?.(err?.response?.data?.message || "Save failed");
                  }
                }}
                className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4 max-w-3xl`}
              >
                <div className={`text-sm font-bold ${baseText}`}>
                  {editingNewsId ? "Edit article" : "New article"}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`block text-xs font-medium ${mutedText}`}>
                    Title *
                    <input
                      required
                      value={newsForm.title}
                      onChange={(e) => setNewsForm((f) => ({ ...f, title: e.target.value }))}
                      className={`mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                  </label>
                  <label className={`block text-xs font-medium ${mutedText}`}>
                    Slug (optional)
                    <input
                      value={newsForm.slug}
                      onChange={(e) => setNewsForm((f) => ({ ...f, slug: e.target.value }))}
                      placeholder="auto from title"
                      className={`mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                  </label>
                </div>
                <label className={`block text-xs font-medium ${mutedText}`}>
                  Cover image — URL or upload to Backblaze
                  <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                    <input
                      value={newsForm.coverImageUrl}
                      onChange={(e) => setNewsForm((f) => ({ ...f, coverImageUrl: e.target.value }))}
                      className={`flex-1 min-w-[200px] rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                      placeholder="https://…"
                    />
                    <BackblazeUploadButton
                      accept="image/*"
                      uploadFn={adminUploadImage}
                      folder="cms/news/covers"
                      onUploaded={(url) => setNewsForm((f) => ({ ...f, coverImageUrl: url }))}
                      onError={(m) => setToast(m)}
                      label="Upload cover"
                    />
                  </div>
                </label>
                <label className={`block text-xs font-medium ${mutedText}`}>
                  Excerpt
                  <textarea
                    rows={2}
                    value={newsForm.excerpt}
                    onChange={(e) => setNewsForm((f) => ({ ...f, excerpt: e.target.value }))}
                    className={`mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                  />
                </label>
                <label className={`block text-xs font-medium ${mutedText}`}>
                  Content
                  <textarea
                    rows={8}
                    value={newsForm.content}
                    onChange={(e) => setNewsForm((f) => ({ ...f, content: e.target.value }))}
                    className={`mt-1.5 w-full rounded-xl border px-3 py-2 font-mono text-sm outline-none ${inputBase}`}
                  />
                </label>
                <div className="flex flex-wrap items-center gap-6">
                  <label className={`flex items-center gap-2 text-sm ${baseText}`}>
                    <input
                      type="checkbox"
                      checked={newsForm.published}
                      onChange={(e) => setNewsForm((f) => ({ ...f, published: e.target.checked }))}
                    />
                    Published
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${mutedText}`}>
                    Sort order
                    <input
                      type="number"
                      value={newsForm.sortOrder}
                      onChange={(e) => setNewsForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))}
                      className={`w-24 rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                  </label>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className={`${theme.btnPrimary} px-5`}
                  >
                    {editingNewsId ? "Update" : "Create"}
                  </button>
                  {editingNewsId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingNewsId(null);
                        setNewsForm({
                          title: "",
                          slug: "",
                          excerpt: "",
                          content: "",
                          coverImageUrl: "",
                          published: true,
                          sortOrder: 0,
                        });
                      }}
                      className={theme.btnSecondary}
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div>
              <h2 className={`text-base font-semibold tracking-tight ${baseText} mb-3`}>Tutorials (videos)</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto mb-6">
                {(tutorials || []).map((tu) => (
                  <div
                    key={tu._id}
                    className={`flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl ${inset}`}
                  >
                    <div>
                      <div className={`font-semibold ${baseText}`}>{tu.title}</div>
                      <div className={`text-xs ${mutedText} truncate max-w-md`}>{tu.videoUrl}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingTutorialId(tu._id);
                          setTutorialForm({
                            title: tu.title || "",
                            description: tu.description || "",
                            videoUrl: tu.videoUrl || "",
                            thumbnailUrl: tu.thumbnailUrl || "",
                            order: tu.order ?? 0,
                            published: !!tu.published,
                          });
                        }}
                        className={theme.iconBtn}
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm("Delete this tutorial?")) return;
                          try {
                            await adminDeleteTutorial(tu._id);
                            setToast?.("Deleted");
                            fetchTutorialsAdmin();
                          } catch (err) {
                            setToast?.(err?.response?.data?.message || "Delete failed");
                          }
                        }}
                        className={theme.dangerIconBtn}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingTutorialId) {
                      await adminUpdateTutorial(editingTutorialId, tutorialForm);
                      setToast?.("Tutorial updated");
                    } else {
                      await adminCreateTutorial(tutorialForm);
                      setToast?.("Tutorial created");
                    }
                    setEditingTutorialId(null);
                    setTutorialForm({
                      title: "",
                      description: "",
                      videoUrl: "",
                      thumbnailUrl: "",
                      order: 0,
                      published: true,
                    });
                    fetchTutorialsAdmin();
                  } catch (err) {
                    setToast?.(err?.response?.data?.message || "Save failed");
                  }
                }}
                className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4 max-w-3xl`}
              >
                <div className={`text-sm font-bold ${baseText}`}>
                  {editingTutorialId ? "Edit tutorial" : "New tutorial"}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`block text-xs font-medium ${mutedText}`}>
                    Title *
                    <input
                      required
                      value={tutorialForm.title}
                      onChange={(e) => setTutorialForm((f) => ({ ...f, title: e.target.value }))}
                      className={`mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                  </label>
                  <label className={`block text-xs font-medium ${mutedText}`}>
                    Video * — YouTube/Vimeo URL or upload to Backblaze
                    <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                      <input
                        required
                        value={tutorialForm.videoUrl}
                        onChange={(e) => setTutorialForm((f) => ({ ...f, videoUrl: e.target.value }))}
                        className={`flex-1 min-w-[200px] rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                        placeholder="https://…"
                      />
                      <BackblazeUploadButton
                        accept="video/*"
                        uploadFn={adminUploadVideo}
                        folder="cms/tutorials"
                        onUploaded={(url) => setTutorialForm((f) => ({ ...f, videoUrl: url }))}
                        onError={(m) => setToast(m)}
                        label="Upload video"
                      />
                    </div>
                  </label>
                </div>
                <label className={`block text-xs font-medium ${mutedText}`}>
                  Thumbnail (optional) — URL or upload
                  <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                    <input
                      value={tutorialForm.thumbnailUrl}
                      onChange={(e) => setTutorialForm((f) => ({ ...f, thumbnailUrl: e.target.value }))}
                      className={`flex-1 min-w-[200px] rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                    <BackblazeUploadButton
                      accept="image/*"
                      uploadFn={adminUploadImage}
                      folder="cms/tutorials/thumbs"
                      onUploaded={(url) => setTutorialForm((f) => ({ ...f, thumbnailUrl: url }))}
                      onError={(m) => setToast(m)}
                      label="Upload thumb"
                    />
                  </div>
                </label>
                <label className={`block text-xs font-medium ${mutedText}`}>
                  Description
                  <textarea
                    rows={2}
                    value={tutorialForm.description}
                    onChange={(e) => setTutorialForm((f) => ({ ...f, description: e.target.value }))}
                    className={`mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                  />
                </label>
                <div className="flex flex-wrap items-center gap-6">
                  <label className={`flex items-center gap-2 text-sm ${baseText}`}>
                    <input
                      type="checkbox"
                      checked={tutorialForm.published}
                      onChange={(e) => setTutorialForm((f) => ({ ...f, published: e.target.checked }))}
                    />
                    Published
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${mutedText}`}>
                    Order
                    <input
                      type="number"
                      value={tutorialForm.order}
                      onChange={(e) => setTutorialForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))}
                      className={`w-24 rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                    />
                  </label>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className={`${theme.btnPrimary} px-5`}
                  >
                    {editingTutorialId ? "Update" : "Create"}
                  </button>
                  {editingTutorialId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTutorialId(null);
                        setTutorialForm({
                          title: "",
                          description: "",
                          videoUrl: "",
                          thumbnailUrl: "",
                          order: 0,
                          published: true,
                        });
                      }}
                      className={theme.btnSecondary}
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === "transactions" && (
          <AdminTransactionsTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "fees" && (
          <AdminFeesTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "users" && (
          <AdminUsersTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "businesses" && (
          <AdminBusinessesTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "featured" && (
          <AdminCampaignsTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "blocked-uploads" && (
          <AdminBlockedUploadsTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "support" && (
          <AdminSupportTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
            socket={supportSocket}
            onUnreadChange={setSupportUnread}
          />
        )}

        {activeTab === "landing" && (
          <AdminLandingTab
            isDark={isDark}
            setToast={setToast}
            baseText={baseText}
            mutedText={mutedText}
            cardBase={cardBase}
          />
        )}

        {activeTab === "bugs" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className={`text-sm font-semibold ${baseText}`}>{t("admin.bugReportStatus")}</label>
              <select
                value={bugStatusFilter}
                onChange={(e) => setBugStatusFilter(e.target.value)}
                className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
              >
                <option value="">{t("admin.bugReportFilterAll")}</option>
                <option value="open">{t("admin.bugStatusOpen")}</option>
                <option value="reviewed">{t("admin.bugStatusReviewed")}</option>
                <option value="closed">{t("admin.bugStatusClosed")}</option>
              </select>
              <span className={`text-sm ${mutedText}`}>
                {t("admin.bugReportsTotal", { count: bugReportsTotal })}
              </span>
            </div>

            {bugReportsLoading ? (
              <div className={`text-sm ${mutedText}`}>{t("common.loading")}</div>
            ) : bugReports.length === 0 ? (
              <div className={`${cardBase} rounded-2xl p-8 text-center ${mutedText}`}>
                {t("admin.bugReportNoItems")}
              </div>
            ) : (
              <div className="grid gap-4">
                {bugReports.map((br) => (
                  <div
                    key={br._id}
                    className={`${cardBase} rounded-2xl p-5`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>{br.title}</h3>
                        <p className={`text-xs ${mutedText} mt-1`}>
                          {t("admin.bugReportUser")}:{" "}
                          <UserProfileLink
                            user={br.user}
                            avatarSize="xs"
                            isDark={isDark}
                            nameClassName="font-mono"
                            fallback={br.user?.username || br.user?.businessName || br.user?.email || "—"}
                          />
                          {br.user?.email && (
                            <span className="ml-2 opacity-80">{br.user.email}</span>
                          )}
                        </p>
                        <p className={`text-xs ${mutedText} mt-0.5`}>
                          {t("admin.bugReportDate")}:{" "}
                          {br.createdAt ? new Date(br.createdAt).toLocaleString() : "—"}
                        </p>
                      </div>
                      <select
                        value={br.status}
                        onChange={(e) => handleBugReportStatusChange(br._id, e.target.value)}
                        className={`rounded-xl border px-3 py-2 text-sm font-semibold shrink-0 outline-none ${inputBase}`}
                      >
                        <option value="open">{t("admin.bugStatusOpen")}</option>
                        <option value="reviewed">{t("admin.bugStatusReviewed")}</option>
                        <option value="closed">{t("admin.bugStatusClosed")}</option>
                      </select>
                    </div>
                    <p className={`mt-3 text-sm whitespace-pre-wrap ${baseText}`}>{br.description}</p>
                    {Array.isArray(br.attachments) && br.attachments.length > 0 && (
                      <div className="mt-4">
                        <p className={`text-xs font-semibold uppercase tracking-wide ${mutedText} mb-2`}>
                          {t("admin.bugReportAttachments")}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {br.attachments.map((a, i) =>
                            a.kind === "video" ? (
                              <a
                                key={i}
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-32 h-24 rounded-lg overflow-hidden border border-white/10 bg-black/20"
                              >
                                <video src={a.url} className="w-full h-full object-cover" muted playsInline />
                              </a>
                            ) : (
                              <a
                                key={i}
                                href={a.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-32 h-24 rounded-lg overflow-hidden border border-white/10"
                              >
                                <img src={a.url} alt="" className="w-full h-full object-cover" />
                              </a>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "appeals" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className={`text-sm font-semibold ${baseText}`}>Status</label>
              <select
                value={appealsStatus}
                onChange={(e) => setAppealsStatus(e.target.value)}
                className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
              >
                <option value="open">Open (awaiting review)</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="all">All</option>
              </select>
              <span className={`text-sm ${mutedText}`}>{appeals.length} appeal(s)</span>
            </div>

            {appealsLoading ? (
              <div className={`text-sm ${mutedText}`}>Loading appeals...</div>
            ) : appeals.length === 0 ? (
              <div className={`${cardBase} rounded-2xl p-8 text-center ${mutedText}`}>
                No appeals to review.
              </div>
            ) : (
              <div className="grid gap-4">
                {appeals.map((a) => {
                  const isOpen = !a.appealResolution;
                  return (
                    <div key={a._id} className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`text-base font-bold ${baseText}`}>
                              {a.campaign?.campaignName || "Unknown campaign"}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                              a.platform === "tiktok"
                                ? "border-slate-400/30 bg-slate-400/10 text-slate-200"
                                : "border-slate-400/30 bg-slate-400/10"
                            }`}>
                              {a.platform}
                            </span>
                            {a.rejectionSource === "bot_detection" && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold border border-orange-400/30 bg-orange-500/10 text-orange-300">
                                bot detection
                              </span>
                            )}
                            {!isOpen && (
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                                a.appealResolution === "approved"
                                  ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
                                  : "border-red-400/30 bg-red-500/10 text-red-300"
                              }`}>
                                {a.appealResolution}
                              </span>
                            )}
                          </div>
                          <div className={`text-xs ${mutedText} mt-1`}>
                            Submitted by{" "}
                            <UserProfileLink
                              user={a.submittedBy}
                              avatarSize="xs"
                              isDark={isDark}
                              fallback={a.submittedBy?.username || a.submittedBy?.email || "—"}
                            />{" "}
                            ·{" "}
                            Appealed {a.appealRequestedAt ? new Date(a.appealRequestedAt).toLocaleString() : "—"}
                          </div>
                          <a
                            href={a.videoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-mint hover:text-aqua mt-2"
                          >
                            <FiExternalLink className="h-4 w-4" />
                            Open video
                          </a>
                        </div>
                        <div className={`text-right text-xs ${mutedText}`}>
                          <div>Views: <span className={baseText}>{(a.viewCount || 0).toLocaleString()}</span></div>
                          {a.botDetectionSnapshot?.score != null && (
                            <div>Bot score: <span className="text-orange-300 font-bold">{a.botDetectionSnapshot.score}/100</span></div>
                          )}
                        </div>
                      </div>

                      <div className={`rounded-xl p-3 mb-3 ${inset}`}>
                        <div className={`text-xs font-bold uppercase ${mutedText} mb-1`}>Creator's appeal</div>
                        <div className={`text-sm whitespace-pre-wrap ${baseText}`}>
                          {a.appealMessage || <span className={mutedText}>(no message)</span>}
                        </div>
                      </div>

                      {a.botDetectionSnapshot?.reasons?.length > 0 && (
                        <div className={`rounded-xl border p-3 mb-3 ${
                          isDark ? "border-orange-400/30 bg-orange-500/10" : "border-orange-200 bg-orange-50"
                        }`}>
                          <div className={`text-xs font-bold uppercase mb-1 ${isDark ? "text-orange-200" : "text-orange-800"}`}>
                            Auto-detection reasons
                          </div>
                          <ul className={`list-disc pl-5 text-xs ${isDark ? "text-orange-200/90" : "text-orange-900"}`}>
                            {a.botDetectionSnapshot.reasons.map((r) => (
                              <li key={r}>{r}</li>
                            ))}
                          </ul>
                          {a.botDetectionSnapshot.metrics && (
                            <div className={`mt-2 text-xs font-mono ${mutedText}`}>
                              {Object.entries(a.botDetectionSnapshot.metrics).map(([k, v]) => (
                                <span key={k} className="mr-3">{k}: {String(v)}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {isOpen ? (
                        <>
                          <textarea
                            rows={2}
                            maxLength={1000}
                            value={appealNotes[a._id] || ""}
                            onChange={(e) =>
                              setAppealNotes((prev) => ({ ...prev, [a._id]: e.target.value }))
                            }
                            placeholder="Reviewer notes (optional, max 1000 chars)"
                            className={`w-full rounded-xl border p-3 text-sm outline-none ${inputBase}`}
                          />
                          <div className="mt-3 flex flex-wrap items-center gap-2 justify-end">
                            <button
                              onClick={() => handleResolveAppeal(a._id, "denied")}
                              disabled={!!appealActionId}
                              className={`px-4 py-2 rounded-xl border text-sm font-semibold transition disabled:opacity-50 ${
                                isDark
                                  ? "border-red-500/40 bg-red-500/15 hover:bg-red-500/25 text-red-300"
                                  : "border-red-300 bg-red-50 hover:bg-red-100 text-red-700"
                              }`}
                            >
                              {appealActionId === a._id + "denied" ? "..." : "Deny appeal"}
                            </button>
                            <button
                              onClick={() => handleResolveAppeal(a._id, "approved")}
                              disabled={!!appealActionId}
                              className={`px-4 py-2 rounded-xl border text-sm font-semibold transition disabled:opacity-50 ${
                                isDark
                                  ? "border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"
                                  : "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {appealActionId === a._id + "approved" ? "..." : "Approve & reinstate"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className={`text-xs ${mutedText}`}>
                          Resolved {a.appealResolvedAt ? new Date(a.appealResolvedAt).toLocaleString() : "—"}
                          {a.appealResolvedBy ? (
                            <>
                              {" by "}
                              <UserProfileLink
                                user={a.appealResolvedBy}
                                avatarSize="xs"
                                isDark={isDark}
                                fallback={a.appealResolvedBy.username || "—"}
                              />
                            </>
                          ) : null}
                          {a.appealReviewerNotes ? ` — "${a.appealReviewerNotes}"` : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
